import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE,
  CHAT_ATTACHMENT_MAX_TOTAL_SIZE_BYTES,
  getChatAttachmentContentType,
} from '@/modules/chat/application/chat-attachment-file-types';
import { readStoredChatPermissionMode } from '@/modules/chat/application/chat-permission';
import {
  ChatAttachmentUploadError,
  type PreparedChatAttachment,
  useChatAttachmentUpload,
} from '@/modules/chat/application/use-chat-attachment-upload';
import { useMessage } from '@/modules/chat/application/use-message';
import { useChatTitle } from '@/modules/chat/hooks/useChatTitle';
import type { ChatRequestContext } from '@/modules/chat/types/request-context';
import { hasChatRequestContext } from '@/modules/chat/types/request-context';
import { useUser } from '@/modules/common/application/context/user';
import {
  ChatPermissionMode,
  ChatReasoningEffort,
  ToolType,
  type SendMessageMutation,
} from '@/modules/common/types';
import { useProjectId } from '@/modules/project/hooks/use-projectId';

import { PromptBox, type PromptBoxAttachment } from './PromptBox';

const STARTER_PROMPTS = ['capabilities', 'getStarted', 'startReport'] as const;

export interface StartedChatConversation {
  conversationId: string;
  initialMessages: SendMessageMutation['sendMessage']['messages'];
}

interface ChatStartScreenProps {
  variant?: 'page' | 'panel';
  requestContext?: ChatRequestContext | null;
  onRequestContextConsumed?: () => void;
  onConversationStarted: (conversation: StartedChatConversation) => void;
}

type PendingChatAttachment = PromptBoxAttachment & {
  attachment?: PreparedChatAttachment;
};

function createLocalAttachmentId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getAttachmentErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Upload failed.';
}

export function ChatStartScreen({
  variant = 'page',
  requestContext = null,
  onRequestContextConsumed,
  onConversationStarted,
}: ChatStartScreenProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { projectId } = useProjectId();
  const chatTitle = useChatTitle(user);
  const [draftConversationId, setDraftConversationId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingChatAttachment[]>([]);
  const draftConversationIdRef = useRef<string | null>(null);
  const { sendMessage, sendMessageLoading } = useMessage({
    conversation: null,
    setConversation: null,
  });
  const { discardAttachment, uploadFile } = useChatAttachmentUpload({
    conversationId: draftConversationId,
    projectId,
    getConversationId: () => draftConversationIdRef.current,
    onAttachmentPrepared: (attachment) => {
      if (!draftConversationIdRef.current) {
        draftConversationIdRef.current = attachment.conversationId;
        setDraftConversationId(attachment.conversationId);
      }
    },
  });
  const isPanel = variant === 'panel';
  const hasBlockingAttachment = pendingAttachments.some(
    (attachment) => attachment.status !== 'uploaded'
  );

  const uploadPendingAttachment = async (localId: string, file: File) => {
    try {
      const attachment = await uploadFile(file);

      setPendingAttachments((currentAttachments) =>
        currentAttachments.map((currentAttachment) =>
          currentAttachment.id === localId
            ? {
                ...currentAttachment,
                attachment,
                mimeType: attachment.mimeType,
                sizeBytes: attachment.sizeBytes,
                status: 'uploaded',
              }
            : currentAttachment
        )
      );
    } catch (error) {
      setPendingAttachments((currentAttachments) =>
        currentAttachments.map((currentAttachment) =>
          currentAttachment.id === localId
            ? {
                ...currentAttachment,
                attachment:
                  error instanceof ChatAttachmentUploadError && error.attachment
                    ? error.attachment
                    : currentAttachment.attachment,
                errorMessage: getAttachmentErrorMessage(error),
                status: 'error',
              }
            : currentAttachment
        )
      );
    }
  };

  const handleFilesSelected = (files: File[]) => {
    if (!projectId) {
      toast.error('Select a project before uploading chat attachments.');
      return;
    }

    const currentTotalSizeBytes = pendingAttachments.reduce(
      (total, attachment) => total + attachment.sizeBytes,
      0
    );
    let nextTotalSizeBytes = currentTotalSizeBytes;
    const remainingSlots = CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE - pendingAttachments.length;
    const selectedFiles = files.slice(0, Math.max(remainingSlots, 0));

    if (files.length > remainingSlots) {
      toast.error(`You can attach up to ${CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE} files.`);
    }

    const attachmentsToUpload = selectedFiles.flatMap((file) => {
      const mimeType = getChatAttachmentContentType(file);

      if (!mimeType) {
        toast.error(`${file.name} is not a supported file type.`);
        return [];
      }

      if (nextTotalSizeBytes + file.size > CHAT_ATTACHMENT_MAX_TOTAL_SIZE_BYTES) {
        toast.error('Chat attachments can be up to 50 MB combined.');
        return [];
      }

      nextTotalSizeBytes += file.size;

      return [
        {
          file,
          id: createLocalAttachmentId(),
          mimeType,
        },
      ];
    });

    if (attachmentsToUpload.length === 0) {
      return;
    }

    setPendingAttachments((currentAttachments) => [
      ...currentAttachments,
      ...attachmentsToUpload.map(({ file, id, mimeType }) => ({
        id,
        name: file.name,
        mimeType,
        sizeBytes: file.size,
        status: 'uploading' as const,
      })),
    ]);

    void uploadPendingAttachments(attachmentsToUpload);
  };

  const uploadPendingAttachments = async (
    attachmentsToUpload: { file: File; id: string; mimeType: string }[]
  ) => {
    for (const { file, id } of attachmentsToUpload) {
      await uploadPendingAttachment(id, file);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    const attachmentToRemove = pendingAttachments.find((attachment) => attachment.id === id);

    setPendingAttachments((currentAttachments) =>
      currentAttachments.filter((attachment) => attachment.id !== id)
    );

    if (!attachmentToRemove?.attachment?.id) {
      return;
    }

    const shouldClearDraftConversation =
      attachmentToRemove.attachment.conversationId === draftConversationIdRef.current &&
      pendingAttachments.length === 1;

    void discardAttachment(attachmentToRemove.attachment.id)
      .then(() => {
        if (shouldClearDraftConversation) {
          draftConversationIdRef.current = null;
          setDraftConversationId(null);
        }
      })
      .catch((error) => {
        toast.error(getAttachmentErrorMessage(error));
      });
  };

  const handleSubmit = async (
    message: string,
    reasoningEffort?: ChatReasoningEffort,
    permissionMode: ChatPermissionMode = readStoredChatPermissionMode()
  ) => {
    if (hasBlockingAttachment) {
      toast.error('Wait for uploads to finish or remove failed attachments.');
      return;
    }

    const activeRequestContext = hasChatRequestContext(requestContext) ? requestContext : undefined;
    const attachmentIds = pendingAttachments
      .map((attachment) => attachment.attachment?.id)
      .filter((id): id is string => Boolean(id));

    try {
      const response = await sendMessage({
        conversationId: draftConversationIdRef.current ?? draftConversationId ?? undefined,
        content: message,
        tool: ToolType.NORMAL,
        projectId,
        reasoningEffort,
        permissionMode,
        requestContext: activeRequestContext,
        attachmentIds,
      });
      const nextConversationId = response.data?.sendMessage.conversation.id;

      if (!nextConversationId) {
        return;
      }

      onConversationStarted({
        conversationId: nextConversationId,
        initialMessages: response.data?.sendMessage.messages ?? [],
      });
      draftConversationIdRef.current = null;
      setPendingAttachments([]);
      setDraftConversationId(null);
    } finally {
      if (activeRequestContext) {
        onRequestContextConsumed?.();
      }
    }
  };

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center overflow-hidden bg-background p-4 dark:bg-[#212121]',
        isPanel && 'justify-end pb-6'
      )}
    >
      <div
        className={cn('flex min-h-0 w-full flex-col gap-6', isPanel ? 'max-w-none' : 'max-w-2xl')}
      >
        <p className={cn('text-center text-foreground', isPanel ? 'text-2xl' : 'text-3xl')}>
          {chatTitle}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {STARTER_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              variant="outline"
              type="button"
              disabled={sendMessageLoading}
              onClick={() => void handleSubmit(t(`modules.mavue2.chat.prompts.${prompt}`))}
              className="h-auto max-w-full whitespace-normal rounded-full px-4 py-2 text-left leading-5"
            >
              {t(`modules.mavue2.chat.prompts.${prompt}`)}
            </Button>
          ))}
        </div>
        <PromptBox
          onSubmit={handleSubmit}
          attachments={pendingAttachments}
          fileInputAccept={CHAT_ATTACHMENT_ACCEPT}
          onFilesSelected={projectId ? handleFilesSelected : undefined}
          onRemoveAttachment={handleRemoveAttachment}
          disabled={sendMessageLoading}
          loading={sendMessageLoading}
          submitDisabled={hasBlockingAttachment}
        />
      </div>
    </div>
  );
}
